import { NetworkStatus } from '@apollo/client';
import { Avatar, CardActionArea, makeStyles, Paper, Slide, Theme, Tooltip } from '@material-ui/core';
import TaskIcon from '@material-ui/icons/Assignment';
import MilestoneIcon from '@material-ui/icons/Flag';
import classNames from 'classnames';
import React, { useCallback, useMemo, useRef } from 'react';

import { TasksOrder, TaskType, useGetTasksQuery } from '../../../graphql/gen/types';
import { usePublishStatusFilter } from '../../../models/filters/usePublishStatusFilter';
import { useSelectedLabelIds } from '../../../models/filters/useSelectedLabelIds';
import { useSelectedPlexIds } from '../../../models/filters/useSelectedPlexIds';
import { useTasksItemTypeFilter } from '../../../models/filters/useTasksItemTypeFilter';
import { useTasksOrder } from '../../../models/filters/useTasksOrder';
import { bottomBorderStyle, colorPalette, spacings, TextSizesEnum } from '../../../theme';
import { getPublishStatuses } from '../../../utils/permissions';
import { LinesEllipsis } from '../../shared/primitives/LinesEllipsis';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { FilterPicker } from '../common/FilterPicker';
import { plainTextPreview } from '../common/plainTextPreview';
import { PlexName } from '../common/PlexName';
import { PostVisibility } from '../common/PostVisibility';
import { TaskDueDateView } from '../details/task/TaskDueDate';
import { TaskStatusView } from '../details/task/TaskStatus';
import { usePlexRoute } from '../details/usePlexRoute';
import { NewMilestoneButton } from '../post_form/NewMilestoneButton';
import { AnalyticsLocation, useLogAnalytics } from '../shared/logAnalytics';
import { CardLabels } from './CardLabels';
import { EmptyTasksView } from './EmptyTasksView';
import { FooterLoader } from './FooterLoader';
import { FetchMoreCallback, useInfiniteScroll } from './useInfiniteScroll';

export const TasksView = React.memo(
  (): React.ReactElement => {
    useLogAnalytics({ name: 'loaded page', details: { location: AnalyticsLocation.TASKS } });
    const classes = useStyles();
    const [, updatePlexRoute] = usePlexRoute();
    const plexIds = useSelectedPlexIds();
    const filterPublishStatus = usePublishStatusFilter();
    const publishStatuses = useMemo(() => getPublishStatuses(filterPublishStatus), [filterPublishStatus]);
    const tasksItemType = useTasksItemTypeFilter();
    const tasksOrder = useTasksOrder();
    const labelIds = useSelectedLabelIds();
    const variables = useMemo(
      () => ({
        plexIds,
        publishStatuses,
        labelIds,
        types: tasksItemType != null ? [tasksItemType] : [TaskType.Task, TaskType.Milestone],
        order: tasksOrder != null ? tasksOrder : TasksOrder.DueDateAsc,
      }),
      [plexIds, publishStatuses, labelIds, tasksItemType, tasksOrder]
    );
    const { data, loading: initialLoading, fetchMore, networkStatus } = useGetTasksQuery({
      variables,
    });
    const tasks = data?.tasks.items || [];
    const nextCursor = data?.tasks.nextCursor;

    const timelineContainerRef = useRef<HTMLDivElement>(null);
    const onFetchMore: FetchMoreCallback = useCallback(
      (isCancelled) =>
        fetchMore({
          variables: { ...variables, cursor: nextCursor },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult || !fetchMoreResult.tasks || isCancelled()) return prev;
            return {
              ...prev,
              tasks: {
                ...prev.tasks,
                items: [...(prev.tasks.items || []), ...fetchMoreResult.tasks.items],
                nextCursor: fetchMoreResult.tasks.nextCursor,
              },
            };
          },
        }),
      [fetchMore, nextCursor, variables]
    );
    const infiniteScrollLoading = useInfiniteScroll({
      containerRef: timelineContainerRef,
      onFetchMore,
      canFetchMore: !initialLoading && nextCursor != null,
    });
    const loading = initialLoading || infiniteScrollLoading;

    // useSubscribeToMoreTasks(subscribeToMore, variables);

    // const handleSelectPlexTask = useCallback(
    //   (id: string): void => {
    //     setSelectedPlexTask({ permalink: id, commentsVisible: false });
    //   },
    //   [setSelectedPlexTask]
    // );

    const filteredTasks = networkStatus === NetworkStatus.setVariables ? [] : tasks.filter((task) => !task.deleted);

    return (
      <Slide direction={'up'} in={true} mountOnEnter={true} unmountOnExit={true}>
        <div className={classes.root} ref={timelineContainerRef}>
          <div className={classes.header}>
            <FilterPicker />
            <NewMilestoneButton className={classes.createBtn} />
          </div>

          {!loading && filteredTasks.length === 0 && <EmptyTasksView />}

          <Paper>
            {filteredTasks.length > 0 && (
              <div className={classes.columnsRow}>
                <PlexTypography
                  className={classNames(classes.columnTitle, classes.columnType)}
                  text={'TYPE'}
                  size={TextSizesEnum.XSmall}
                />
                <PlexTypography
                  className={classNames(classes.columnTitle, classes.columnDescription)}
                  style={{ flex: 1 }}
                  text={'DESCRIPTION'}
                  size={TextSizesEnum.XSmall}
                />
                <PlexTypography
                  className={classNames(classes.columnTitle, classes.columnPlex)}
                  text={'PLEX'}
                  size={TextSizesEnum.XSmall}
                />
                <PlexTypography
                  className={classNames(classes.columnTitle, classes.columnAssignee)}
                  text={'ASSIGNEE'}
                  size={TextSizesEnum.XSmall}
                />
                <PlexTypography
                  className={classNames(classes.columnTitle, classes.columnStatus)}
                  text={'STATUS'}
                  size={TextSizesEnum.XSmall}
                />
                <PlexTypography
                  className={classNames(classes.columnTitle, classes.columnVisibility)}
                  text={'VISIBILITY'}
                  size={TextSizesEnum.XSmall}
                />
                <PlexTypography
                  className={classNames(classes.columnTitle, classes.columnDate)}
                  text={'DUE DATE'}
                  size={TextSizesEnum.XSmall}
                />
              </div>
            )}
            {filteredTasks.map((task) => (
              <CardActionArea
                key={task.id}
                id={`task-${task.id}`}
                component="div"
                className={classes.eventRoot}
                onClick={(): void => {
                  updatePlexRoute({ selectedItem: { type: 'task', permalink: task.permalink } });
                }}
              >
                <div className={classNames(classes.columnType, classes.columnTypeIcon)}>
                  {task.type === TaskType.Task ? <TaskIcon fontSize="small" /> : <MilestoneIcon />}
                </div>
                <div
                  className={classNames(classes.columnDescription)}
                  style={{ display: 'flex', flexDirection: 'column' }}
                >
                  <div className={classes.titleContainer}>
                    <PlexTypography className={classes.title} text={task.title} size={TextSizesEnum.Normal} />
                    <CardLabels labels={task.labels} />
                  </div>

                  <PlexTypography className={classes.eventText} size={TextSizesEnum.XSmall}>
                    <LinesEllipsis text={plainTextPreview(task.body, task.mentions, task.links)} maxLine={2} />
                  </PlexTypography>
                </div>
                <div className={classNames(classes.columnPlex)}>
                  <PlexName className={classes.eventPlexName} name={task.plex.name} color={task.plex.color} />
                </div>
                <div className={classNames(classes.columnAssignee)}>
                  {task.assignee != null ? (
                    <Tooltip title={task.assignee.name}>
                      <Avatar alt={task.assignee.name} src={task.assignee.picture} className={classes.avatar} />
                    </Tooltip>
                  ) : (
                    'Unassigned'
                  )}
                </div>
                <div className={classNames(classes.columnStatus)}>
                  <TaskStatusView status={task.status} />
                </div>
                <div className={classNames(classes.columnVisibility, classes.eventPublishStatus)}>
                  <PostVisibility plexPublishStatus={task.publishStatus} plexName={task.plex.name} />
                </div>
                <div className={classNames(classes.columnDate)}>
                  <TaskDueDateView dueDate={task.dueDate} />
                </div>
              </CardActionArea>
            ))}
          </Paper>
          <FooterLoader isLoading={loading} />
        </div>
      </Slide>
    );
  }
);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: `0 ${spacings.normal}px`,
    overflowY: 'auto',
  },
  createBtn: {
    flexShrink: 0,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    padding: spacings.normal,
    margin: `0 -${spacings.normal}px`,
    justifyContent: 'space-between',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    left: 0,
    backgroundColor: colorPalette.general.feedBackground,
    zIndex: 10,
    alignItems: 'center',
  },
  columnsRow: {
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    height: 32,
    alignItems: 'center',
    paddingLeft: 14,
    paddingRight: 14, // change to account for action buttons in the future
    ...bottomBorderStyle,
  },
  columnTitle: {
    display: 'flex',
    color: '#464663',
    fontWeight: 'bold',
  },
  titleContainer: {
    display: 'inline-block',
    lineHeight: '26px',
  },
  columnType: {
    display: 'flex',
    width: 60,
    marginLeft: 10,
  },
  columnTypeIcon: {
    color: colorPalette.purpleGray._500,
    fontSize: theme.typography.fontSize,
  },
  columnDescription: {
    display: 'flex',
    flex: 1,
    minWidth: 120,
  },
  columnPlex: {
    display: 'flex',
    width: 160,
    marginRight: 16,
  },
  columnAssignee: {
    display: 'flex',
    width: 120,
  },
  columnStatus: {
    display: 'flex',
    width: 120,
  },
  columnVisibility: {
    display: 'flex',
    width: 60,
  },
  columnDate: {
    display: 'flex',
    width: 90,
    flexDirection: 'column',
  },
  eventRoot: {
    display: 'flex',
    flexDirection: 'row',
    padding: 14,
    boxSizing: 'border-box',
    ...bottomBorderStyle,
  },
  title: {
    display: 'inline',
    color: colorPalette.primaryColorBlue._500,
  },
  eventText: {
    color: colorPalette.purpleGray._500,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 500,
    marginTop: spacings.small,
  },
  eventPlexName: {
    maxWidth: 160,
    height: 'auto',
  },
  eventPublishStatus: {
    display: 'flex',
    color: colorPalette.purpleGray._500,
    fontSize: theme.typography.fontSize,
  },
  avatar: {
    width: 24,
    height: 24,
  },
}));
